/* ============================================================
   Scientific Advisory Board — interactive behaviors
   Encapsulated vanilla web components + one scoped reveal helper.
   No globals leaked; listeners cleaned up on disconnect.

   Accessibility (reviewed pre-build, WCAG 2.1 AA):
   - Reduced-motion is checked FIRST and the code bails before
     mutating the DOM, so the server-rendered final state stands.
   - <ab-counter> animates an aria-hidden visual node only; the
     real final value is server-rendered (visible) + mirrored in a
     visually-hidden node, so AT never catches an intermediate value
     and a JS failure still shows the correct number.
   - <ab-readmore> toggles the `hidden` attribute on the remainder
     (never a CSS clamp that leaves text readable to AT) and keeps
     aria-expanded in sync.
   - <ab-scrollspy> moves focus to the target heading and sets
     aria-current="location" on the active rail link (the spec token
     for in-page section navigation).
   - <ab-roster-scroller> wires decorative position dots onto the
     native overflow list on the Meet the Board strip. Not a carousel
     (no carousel ARIA, no live region, instant scroll).
   ============================================================ */
(function () {
  "use strict";

  var prefersReduced = function () {
    return (
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  };

  /* ---------- Reveal: transform-only entrance nudge ---------- */
  (function initReveal() {
    if (prefersReduced()) return; // leave content in place, fully visible
    var arm = function () {
      var els = document.querySelectorAll(
        ".ab [data-ab-reveal]:not(.is-armed)"
      );
      if (!("IntersectionObserver" in window)) {
        Array.prototype.forEach.call(els, function (el) {
          el.classList.add("is-armed", "is-revealed");
        });
        return;
      }
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              entry.target.classList.add("is-revealed");
              io.unobserve(entry.target);
            }
          });
        },
        { rootMargin: "0px 0px -8% 0px" }
      );
      Array.prototype.forEach.call(els, function (el) {
        el.classList.add("is-armed");
        io.observe(el);
      });
    };
    if (document.readyState !== "loading") arm();
    else document.addEventListener("DOMContentLoaded", arm);
  })();

  /* ---------- <ab-counter>: count-up stat ---------- */
  if (!customElements.get("ab-counter")) {
    customElements.define(
      "ab-counter",
      class extends HTMLElement {
        connectedCallback() {
          // Final value is already server-rendered. Skip animation
          // entirely under reduced-motion or for non-countable values.
          if (prefersReduced() || this.hasAttribute("data-no-count")) return;
          var num = this.querySelector("[data-ab-counter-num]");
          if (!num) return;
          var target = parseFloat(this.getAttribute("data-target"));
          if (isNaN(target)) return;
          var suffix = this.getAttribute("data-suffix") || "";
          var self = this;
          this._io = new IntersectionObserver(
            function (entries) {
              entries.forEach(function (entry) {
                if (entry.isIntersecting) {
                  self._run(num, target, suffix);
                  self._io.disconnect();
                }
              });
            },
            { threshold: 0.4 }
          );
          this._io.observe(this);
        }

        _run(num, target, suffix) {
          var t0 = null;
          var dur = 1300;
          var step = function (t) {
            if (t0 === null) t0 = t;
            var p = Math.min(1, (t - t0) / dur);
            var eased = 1 - Math.pow(1 - p, 3);
            num.textContent = Math.round(target * eased) + suffix;
            if (p < 1) requestAnimationFrame(step);
            else num.textContent = target + suffix;
          };
          requestAnimationFrame(step);
        }

        disconnectedCallback() {
          if (this._io) this._io.disconnect();
        }
      }
    );
  }

  /* ---------- <ab-readmore>: expandable bio ---------- */
  if (!customElements.get("ab-readmore")) {
    customElements.define(
      "ab-readmore",
      class extends HTMLElement {
        connectedCallback() {
          this._btn = this.querySelector("[data-ab-readmore-btn]");
          this._region = this.querySelector("[data-ab-readmore-region]");
          this._label = this.querySelector("[data-ab-readmore-label]");
          if (!this._btn || !this._region) return;
          this._more = this._btn.getAttribute("data-label-more") || "Learn More";
          this._less = this._btn.getAttribute("data-label-less") || "Show Less";
          this._onClick = this._toggle.bind(this);
          this._btn.addEventListener("click", this._onClick);
        }

        _toggle() {
          var open = this._btn.getAttribute("aria-expanded") === "true";
          this._btn.setAttribute("aria-expanded", String(!open));
          if (this._label) {
            this._label.textContent = open ? this._more : this._less;
          }
          this.classList.toggle("is-open", !open);

          // Smooth height animation via grid-template-rows (0fr <-> 1fr).
          // The collapsed resting state stays `hidden` from AT: on collapse we
          // apply `hidden` only after the animation ends; under reduced-motion
          // we skip the animation and toggle `hidden` immediately (no reliance
          // on transitionend, which wouldn't fire when transitions are disabled).
          var self = this;
          if (this._onEnd) {
            this._region.removeEventListener("transitionend", this._onEnd);
            this._onEnd = null;
          }

          if (open) {
            this._region.classList.remove("is-open");
            if (prefersReduced()) {
              this._region.hidden = true;
              return;
            }
            this._onEnd = function (e) {
              if (e.target !== self._region || e.propertyName !== "grid-template-rows") return;
              self._region.hidden = true;
              self._region.removeEventListener("transitionend", self._onEnd);
              self._onEnd = null;
            };
            this._region.addEventListener("transitionend", this._onEnd);
          } else {
            this._region.hidden = false;
            if (prefersReduced()) {
              this._region.classList.add("is-open");
              return;
            }
            void this._region.offsetHeight; // commit the 0fr start state
            this._region.classList.add("is-open");
          }
        }

        disconnectedCallback() {
          if (this._btn && this._onClick) {
            this._btn.removeEventListener("click", this._onClick);
          }
          if (this._region && this._onEnd) {
            this._region.removeEventListener("transitionend", this._onEnd);
          }
        }
      }
    );
  }

  /* ---------- <ab-scrollspy>: profile bio rail ---------- */
  if (!customElements.get("ab-scrollspy")) {
    customElements.define(
      "ab-scrollspy",
      class extends HTMLElement {
        connectedCallback() {
          this._links = Array.prototype.slice.call(
            this.querySelectorAll("[data-ab-spy-link]")
          );
          this._targets = this._links.map(function (a) {
            var href = a.getAttribute("href") || "";
            return href.charAt(0) === "#"
              ? document.getElementById(href.slice(1))
              : null;
          });
          if (!this._links.length) return;
          this._onClick = this._handleClick.bind(this);
          this._onScroll = this._handleScroll.bind(this);
          var self = this;
          this._links.forEach(function (a) {
            a.addEventListener("click", self._onClick);
          });
          window.addEventListener("scroll", this._onScroll, { passive: true });
          this._handleScroll();
        }

        _handleClick(e) {
          var a = e.currentTarget;
          var idx = this._links.indexOf(a);
          var target = this._targets[idx];
          if (!target) return; // let the native anchor jump happen
          e.preventDefault();
          // Move focus to the destination so keyboard/AT users land there
          // (smooth-scroll preventDefault would otherwise strand focus).
          target.setAttribute("tabindex", "-1");
          target.focus({ preventScroll: true });
          target.scrollIntoView({
            behavior: prefersReduced() ? "auto" : "smooth",
            block: "start",
          });
        }

        _handleScroll() {
          var idx = 0;
          this._targets.forEach(function (s, i) {
            if (s && s.getBoundingClientRect().top - 140 <= 0) idx = i;
          });
          this._links.forEach(function (a, i) {
            if (i === idx) {
              a.setAttribute("aria-current", "location");
              a.classList.add("is-active");
            } else {
              a.removeAttribute("aria-current");
              a.classList.remove("is-active");
            }
          });
        }

        disconnectedCallback() {
          var self = this;
          if (this._links) {
            this._links.forEach(function (a) {
              a.removeEventListener("click", self._onClick);
            });
          }
          if (this._onScroll) {
            window.removeEventListener("scroll", this._onScroll);
          }
        }
      }
    );
  }

  /* ---------- <ab-roster-scroller>: overflow dots ----------
     Progressive enhancement on the Meet the Board strip. The ul remains
     the widget (R19). This element only shows decorative dots when the
     list actually overflows, scrolls with behavior:"auto" (R26), and
     never adds carousel ARIA (R18). */
  if (!customElements.get("ab-roster-scroller")) {
    customElements.define(
      "ab-roster-scroller",
      class extends HTMLElement {
        connectedCallback() {
          this._teardown();
          this._list = this.querySelector("[data-abr-list]");
          this._nav = this.querySelector("[data-abr-nav]");
          this._dotsWrap = this.querySelector("[data-abr-dots]");
          if (!this._list || !this._nav || !this._dotsWrap) return;

          this._onScroll = this._sync.bind(this);
          this._onDotClick = this._onDot.bind(this);

          this._list.addEventListener("scroll", this._onScroll, { passive: true });
          this._dotsWrap.addEventListener("click", this._onDotClick);

          if (typeof ResizeObserver === "function") {
            this._ro = new ResizeObserver(this._onScroll);
            this._ro.observe(this._list);
          } else {
            window.addEventListener("resize", this._onScroll);
          }

          this._buildDots();
          this._sync();
        }

        disconnectedCallback() {
          this._teardown();
        }

        _teardown() {
          if (this._list && this._onScroll) {
            this._list.removeEventListener("scroll", this._onScroll);
          }
          if (this._dotsWrap && this._onDotClick) {
            this._dotsWrap.removeEventListener("click", this._onDotClick);
          }
          if (this._ro) {
            this._ro.disconnect();
            this._ro = null;
          } else if (this._onScroll) {
            window.removeEventListener("resize", this._onScroll);
          }
        }

        _items() {
          return Array.prototype.slice.call(
            this._list.querySelectorAll(":scope > li")
          );
        }

        _isRtl() {
          return getComputedStyle(this._list).direction === "rtl";
        }

        _buildDots() {
          if (!this._dotsWrap) return;
          var items = this._items();
          this._dotsWrap.textContent = "";
          var frag = document.createDocumentFragment();
          items.forEach(function (_item, i) {
            var span = document.createElement("span");
            span.className = "advisory-board-roster__dot";
            span.setAttribute("data-abr-dot", String(i));
            frag.appendChild(span);
          });
          this._dotsWrap.appendChild(frag);
        }

        _ensureDots() {
          if (!this._dotsWrap) return;
          var n = this._items().length;
          var current = this._dotsWrap.querySelectorAll("[data-abr-dot]").length;
          if (current !== n) this._buildDots();
        }

        _overflows() {
          return this._list.scrollWidth - this._list.clientWidth > 1;
        }

        _scrollPos() {
          return Math.abs(this._list.scrollLeft);
        }

        _maxScroll() {
          return Math.max(0, this._list.scrollWidth - this._list.clientWidth);
        }

        _sync() {
          this._ensureDots();
          var overflows = this._overflows();
          this._setNavHidden(!overflows);
          if (!overflows) return;
          this._syncDots();
        }

        _setNavHidden(hide) {
          var isHidden = this._nav.hasAttribute("hidden");
          if (hide) {
            if (!isHidden) this._nav.setAttribute("hidden", "");
          } else if (isHidden) {
            this._nav.removeAttribute("hidden");
          }
        }

        _padStart() {
          var cs = getComputedStyle(this._list);
          return parseFloat(cs.paddingInlineStart) || 0;
        }

        _activeIndex() {
          var items = this._items();
          if (!items.length) return 0;
          var listRect = this._list.getBoundingClientRect();
          var rtl = this._isRtl();
          var pad = this._padStart();
          var origin = rtl ? listRect.right - pad : listRect.left + pad;
          var best = 0;
          var bestDist = Infinity;
          items.forEach(function (item, i) {
            var r = item.getBoundingClientRect();
            var start = rtl ? r.right : r.left;
            var dist = Math.abs(start - origin);
            if (dist < bestDist) {
              bestDist = dist;
              best = i;
            }
          });
          return best;
        }

        _syncDots() {
          if (!this._dotsWrap) return;
          var items = this._items();
          var n = items.length;
          var active = this._activeIndex();
          // Leftmost card is the scroll position, but two cards can fill a
          // phone so the last member never reaches the start edge. Light the
          // last dot when we are at max scroll (and the first at the start).
          if (n) {
            var pos = this._scrollPos();
            var max = this._maxScroll();
            if (pos <= 1) active = 0;
            else if (pos >= max - 1) active = n - 1;
          }
          var dots = this._dotsWrap.querySelectorAll("[data-abr-dot]");
          Array.prototype.forEach.call(dots, function (dot, i) {
            if (i === active) dot.classList.add("is-active");
            else dot.classList.remove("is-active");
          });
        }

        _scrollToItem(item) {
          if (!item) return;
          var list = this._list;
          var listRect = list.getBoundingClientRect();
          var itemRect = item.getBoundingClientRect();
          var pad = this._padStart();
          var delta = this._isRtl()
            ? itemRect.right - (listRect.right - pad)
            : itemRect.left - (listRect.left + pad);
          list.scrollTo({
            left: list.scrollLeft + delta,
            behavior: "auto",
          });
        }

        _onDot(e) {
          var dot = e.target.closest("[data-abr-dot]");
          if (!dot || !this._dotsWrap.contains(dot)) return;
          var idx = parseInt(dot.getAttribute("data-abr-dot"), 10);
          var items = this._items();
          if (!isNaN(idx) && items[idx]) this._scrollToItem(items[idx]);
        }
      }
    );
  }
})();
